/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  SyntheticEvent,
  useState
} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {
  CTCEntity,
  Entity,
  IOProfile,
  Operator,
  User
} from '@ion/core/src/types/entities';
import {
  DateFilterMegaMenu
} from '@ion/dls/src/components/menus/DateFilterMegaMenu';
import {useExtendStyles} from '../../hooks/useExtendStyles';
import {DLSTheme} from '../../types/types';
import EditorDialog from '../EditorDialog';
import styles from '../editorStyles';
import AuditGrid, {AuditGridOptions} from './AuditGrid';
import {useDateFilter} from './useDateFilter';

export interface AuditDateFilterProps {
  enableDateFilter?: boolean;
};

export interface AuditDialogProps extends AuditGridOptions, AuditDateFilterProps {
  item: CTCEntity;
  entity: Entity;
  onCloseDialog?: (event: SyntheticEvent<{}, Event>) => void;
  onViewAuditChanges?: Function;
};

const overrides = (theme: DLSTheme) => ({
  form : {
    height : '100%'
  },
  formPaper : {
    minWidth    : '70% !important',
    paddingLeft : '2px !important',
    maxHeight   : '574px',
    height      : '574px'
  },
  paperScrollPaper : {
    minHeight                      : 'calc(75% - 94px)',
    [theme.breakpoints.down('sm')] : {
      minHeight : 'calc(75% - 94px)'
    }
  },
  filter : {
    textAlign    : 'end',
    marginBottom : '10px'
  }
});

/**
 * @AuditDialog
 *
 * The AuditDialog provides the UI for reviewing the change history of a selected entity.
 *
 * ```typescript
 *
 *     import AuditDialog from '@ion/dls/src/editors/audit/AuditDialog';
 *
 *     <AuditDialog item={auditAsset} entity={Entity.asset} onCloseDialog={onAuditClose} />
 *
 * ```
 */
export const AuditDialog = ({
  enableDateFilter,
  item,
  entity,
  onCloseDialog,
  onViewAuditChanges,
  rowMenusEnabled = false,
  showAuditVersion = false,
  showDifferences = false
}: AuditDialogProps) => {
  const classes                             = useExtendStyles(styles, overrides)();
  const [hasFileHistory, setHasFileHistory] = useState<boolean>(false);
  const {
    selectedFilterValue,
    setSelectedFilterValue,
    selectedRange,
    setSelectedDateValue,
    handleSubmit
  } = useDateFilter();

  function getTitleName(item: CTCEntity) {
    switch (entity) {
      case Entity.ioprofile: {
        const {profileName = ''} = item as IOProfile;
        return profileName;
      }
      case Entity.operator: {
        const {firstName = '', lastName = ''} = item as Operator;
        return `${firstName} ${lastName}`;
      }
      case Entity.user: {
        const {username = ''} = item as User;
        return username;
      }
    }
    return item?.name;
  };

  return (
    <EditorDialog open
      dialogTitle={translate(`common:editors.audit.${hasFileHistory ? 'titleWithFile' : 'title'}`, {name: getTitleName(item)})}
      onCloseDialog={onCloseDialog}
      onCancelDialog={onCloseDialog}
      styleOverrides={overrides}
      omitActions>
      {enableDateFilter && <div className={classes.filter}>
        <DateFilterMegaMenu onSubmit={handleSubmit}
          setSelectedDateValue={setSelectedDateValue}
          selectedFilterValue={selectedFilterValue}
          setSelectedFilterValue={setSelectedFilterValue}
          dateRangeFromToday={true}
          menuLeft={'0px'}
          minMenuWidth={'100%'} />
      </div>}
      <form className={classes.form} style={{overflow: 'unset', height: '100%'}}>
        <AuditGrid item={item}
          showDifferences={showDifferences}
          rowMenusEnabled={rowMenusEnabled}
          showAuditVersion={showAuditVersion}
          selectedRange={selectedRange}
          enableDateFilter={enableDateFilter}
          setHasFileHistory={setHasFileHistory}
          entity={entity}
          onViewAuditChanges={onViewAuditChanges} />
      </form>
    </EditorDialog>
  );
};

export default AuditDialog;
